/**
 * retail.js — Retail AI prompt tasks (buyer decks, sell sheets, pitches, cold emails, objections, promo plans).
 * Schema and quality bar: see _exemplar.js. Template syntax: lib/ai-lab/renderTemplate.js.
 * Persona across the category: a former category buyer who now coaches emerging brands.
 */
const RETAILER_OPTIONS = [
  "Whole Foods Market",
  "Sprouts Farmers Market",
  "Target",
  "Walmart",
  "Kroger",
  "Costco",
  "7-Eleven",
  "Wegmans",
  "H-E-B",
  "Publix",
  "Independent / regional grocer",
  "Other (describe in positioning)",
];

export const RETAIL_TASKS = [
  // ── BUYER PRESENTATION ─────────────────────────────────────────────────────
  {
    id: "retail-buyer-presentation",
    category: "retail",
    title: "Create Buyer Presentation",
    description: "Slide-by-slide buyer deck content — why now, brand, product, consumer, velocity proof, margin and pricing, promo plan, logistics and the ask — tailored to a specific retailer.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gamma"],
    estimatedTime: "4 min to fill · 2 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine, 3 flavors", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "Retail price (MSRP)", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "channels", label: "Current sales channels", type: "tags", placeholder: "DTC website, Amazon, 40 independent stores", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], default: "In local / regional retail", brandKey: "stage" },
      { id: "retailer", label: "Target retailer", type: "select", options: RETAILER_OPTIONS, default: "Sprouts Farmers Market", required: true },
      { id: "velocityData", label: "Sales / velocity proof", type: "textarea", placeholder: "e.g. 4.2 units/store/week across 40 stores; $180k DTC trailing 12 months; 38% repeat rate", span: "full" },
      { id: "wholesaleCost", label: "Wholesale / case cost", type: "text", placeholder: "$1.65 per unit · $19.80 per case of 12" },
      { id: "caseConfig", label: "Case pack & logistics", type: "text", placeholder: "12 per case, 12-month shelf life, ambient, ships from Ohio" },
      { id: "promoSupport", label: "Promo & marketing support offered", type: "textarea", placeholder: "Intro TPR, 2 demo days per store, geo-targeted social, free fill" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Consumer-ad language; vague 'huge market' claims", brandKey: "avoid" },
    ],
    template: `Act as a former {{retailer}} category buyer who now coaches emerging brands on how to win a shelf placement.

Write the complete slide-by-slide content for a buyer presentation from {{brandName}} ({{category|article}}) to the {{retailer}} {{category|lower}} buyer.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
MSRP: {{pricePoint}}
{{/if}}
{{#if wholesaleCost}}
Wholesale: {{wholesaleCost}}
{{/if}}
{{#if caseConfig}}
Logistics: {{caseConfig}}
{{/if}}

TARGET CONSUMER
{{targetConsumer}}

POSITIONING
{{positioning}}

{{#if claims}}
KEY CLAIMS
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET ON THE {{retailer|upper}} SHELF
{{competitors|join}}
{{/if}}

BUSINESS CONTEXT
- Stage: {{stage}}
{{#if channels}}
- Current channels: {{channels|join}}
{{/if}}
- Proof / velocity: {{velocityData|default:none supplied — build the velocity slide around DTC, sampling or pre-launch signals and mark every number as [INSERT DATA]}}
{{#if promoSupport}}
- Promo and marketing support: {{promoSupport}}
{{/if}}

DECK — WRITE EVERY SLIDE IN FULL
For each slide give: slide title, headline (one sentence the buyer should remember), 3 to 5 bullets or a table, speaker note (2 to 3 sentences), and a visual suggestion.
1. Title slide — brand, one-line positioning, meeting date placeholder
2. Why now — the consumer and category shift that makes {{category|lower}} worth a reset at {{retailer}} (cite trend types, not invented statistics; mark [SOURCE NEEDED] where a stat belongs)
3. The brand — positioning, founder credibility, what we stand for in one sentence
4. The product — line-up, formats, flavors, claims, certifications (table)
5. The {{retailer}} shopper — who buys this, how it fits their basket, incrementality vs. the current set
6. Proof — velocity, DTC data, reviews, repeat rate, existing retail results (table; placeholders where data is missing)
7. Competitive position — where {{brandName}} sits vs. {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}the current set{{/unless}} on price, claims and occasion (2x2 or table)
8. Pricing and margin — MSRP, wholesale, retailer margin %, how it compares to category norms at {{retailer}} (show the math; never invent the retailer's actual margin targets — state typical ranges and mark [CONFIRM])
9. Promotional plan — launch TPR, demos, coupons, digital support, first-12-month calendar
10. Marketing — how we drive {{targetConsumer}} to the store (social, geo-targeted ads, local activation)
11. Logistics — case pack, shelf life, distributor / DSD, lead times, EDI readiness, insurance and certifications checklist
12. The ask — specific: number of stores / region / set, SKUs, shelf placement, launch window, next steps
13. Appendix — spec sheet, nutrition panel placeholder, contact

THEN PROVIDE
14. A 60-second verbal open the founder can memorize
15. The 3 numbers the buyer will ask for that are missing from the inputs above

RULES
Use only the numbers supplied. Where a figure is missing, write [INSERT] rather than estimating. Flag any claim that needs substantiation with [VERIFY]. Margin math must be shown, not asserted.

{{#if avoid}}
AVOID
{{avoid|list}}
- Consumer-advertising language in a trade deck
- "Huge market" statements with no source
{{/if}}
{{#unless avoid}}
AVOID
- Consumer-advertising language in a trade deck
- "Huge market" statements with no source
- More than 5 bullets per slide
{{/unless}}`,
    tips: [
      "Paste the slide content into Gamma in “Paste in text” mode to get a designed deck in minutes.",
      "Follow up: “Cut this to an 8-slide version for a 15-minute line review.”",
    ],
  },

  // ── SELL SHEET ─────────────────────────────────────────────────────────────
  {
    id: "retail-sell-sheet",
    category: "retail",
    title: "Create Sell Sheet",
    description: "One-page sell sheet content — headline, retailer-facing benefits, product facts table, case pack, UPC, MSRP, margin, shelf life, distributor and contact — laid out for Canva or Gamma.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "canva", "gamma"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine, 3 flavors", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee extract, L-theanine", brandKey: "ingredients" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "wholesaleCost", label: "Wholesale cost", type: "text", placeholder: "$1.65 per unit · $19.80 per case" },
      { id: "caseConfig", label: "Case pack", type: "text", placeholder: "12 units per case · 8 cases per layer · 64 per pallet" },
      { id: "upc", label: "UPC(s)", type: "text", placeholder: "8 12345 67890 1 (Citrus), 8 12345 67890 2 (Berry)" },
      { id: "shelfLife", label: "Shelf life & storage", type: "text", placeholder: "12 months, ambient" },
      { id: "distributor", label: "Distributor / how to order", type: "text", placeholder: "UNFI item #123456; KeHE; or direct via Faire" },
      { id: "contactInfo", label: "Contact", type: "text", placeholder: "Jordan Lee · jordan@brand.com · 555-123-4567" },
      { id: "channelFocus", label: "Channel this sheet is for", type: "select", options: ["Natural / specialty grocery", "Conventional grocery", "Convenience", "Club", "Foodservice / coffee shops", "Independent retailers"], default: "Natural / specialty grocery" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Consumer taglines; paragraphs; invented certifications", brandKey: "avoid" },
    ],
    template: `Act as a former category buyer who has reviewed thousands of sell sheets and now coaches emerging brands on what actually gets read.

Write the complete content for a one-page sell sheet for {{brandName}}, {{category|article}}, aimed at {{channelFocus|lower}} buyers.

PRODUCT
{{productDescription}}

POSITIONING
{{positioning}}

{{#if targetConsumer}}
WHO BUYS IT
{{targetConsumer}}
{{/if}}

{{#if claims}}
CLAIMS (use exactly — do not add)
{{claims|list}}
{{/if}}
{{#if ingredients}}
HERO INGREDIENTS
{{ingredients|join}}
{{/if}}

TRADE FACTS (use exactly; write [INSERT] for anything missing)
{{#if pricePoint}}
- MSRP: {{pricePoint}}
{{/if}}
{{#if wholesaleCost}}
- Wholesale: {{wholesaleCost}}
{{/if}}
{{#if caseConfig}}
- Case pack: {{caseConfig}}
{{/if}}
{{#if upc}}
- UPC: {{upc}}
{{/if}}
{{#if shelfLife}}
- Shelf life: {{shelfLife}}
{{/if}}
{{#if distributor}}
- How to order: {{distributor}}
{{/if}}
{{#if contactInfo}}
- Contact: {{contactInfo}}
{{/if}}

PROVIDE (a buyer reads a sell sheet in 20 seconds — every word must earn its place)
1. Headline (max 8 words) — 3 options, mark the recommended one; benefit to the retailer, not the consumer
2. Sub-headline (max 20 words) — positioning in buyer language
3. Retailer-facing benefits — 4 bullets, max 12 words each (incrementality, margin, velocity, trend fit, support)
4. Consumer-facing bullets — 3 bullets on why shoppers pick it up
5. Product facts table — one row per SKU: SKU / flavor | Size | UPC | Case pack | Wholesale | MSRP | Retailer margin % | Shelf life
6. Margin line — show the margin formula with the supplied numbers; if wholesale or MSRP is missing, show the formula with [INSERT]
7. Proof strip — 3 short slots for velocity, reviews or retail partners (write as [INSERT] if not supplied; never invent)
8. Support block — marketing and promo support offered at launch (demos, TPR, digital, free fill) in 3 bullets
9. Ordering block — distributor details, minimums, lead time, contact
10. Layout map — a simple top-to-bottom wireframe showing where each block sits on a letter-size page, plus image notes (hero product shot, lifestyle, logo row)
11. Suggested file name and a 2-line email that sends the sheet

RULES
Only the numbers supplied above. Do not invent certifications, awards or retailer names. Mark any claim needing substantiation [VERIFY].

{{#if avoid}}
AVOID
{{avoid|list}}
- Paragraphs of body copy
- Consumer ad taglines as the headline
{{/if}}
{{#unless avoid}}
AVOID
- Paragraphs of body copy
- Consumer ad taglines as the headline
- More than one page of content
{{/unless}}`,
    tips: [
      "Open a Canva “sell sheet” template, set your Brand Kit, and paste each block into place using the layout map.",
      "Follow up: “Make a convenience-store version — lead with velocity and single-serve margin.”",
    ],
  },

  // ── RETAILER-SPECIFIC PITCH ────────────────────────────────────────────────
  {
    id: "retail-retailer-pitch",
    category: "retail",
    title: "Create Retailer-Specific Pitch",
    description: "A pitch narrative tailored to one retailer's shopper, private-label reality, reset calendar and local angle — what to say, in what order, and what that buyer is scored on.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "perplexity"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "retailer", label: "Target retailer", type: "select", options: RETAILER_OPTIONS, default: "Whole Foods Market", required: true },
      { id: "region", label: "Region / division you are pitching", type: "text", placeholder: "Southern California; Northeast region; all Texas stores" },
      { id: "localAngle", label: "Local / regional angle", type: "textarea", placeholder: "Founded in Austin; ingredients sourced from Texas farms; 12 local stores already carry us", span: "full" },
      { id: "velocityData", label: "Sales / velocity proof", type: "textarea", placeholder: "4.2 units/store/week across 40 stores; 38% DTC repeat rate", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Generic 'we'd love to partner' language", brandKey: "avoid" },
    ],
    template: `Act as a former {{retailer}} category buyer who now coaches emerging brands on pitching that specific retailer.

Build a retailer-specific pitch for {{brandName}} ({{category|article}}) to {{retailer}}{{#if region}}, {{region}}{{/if}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
MSRP: {{pricePoint}}
{{/if}}

TARGET CONSUMER
{{targetConsumer}}

POSITIONING
{{positioning}}

{{#if claims}}
KEY CLAIMS
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITORS LIKELY ALREADY ON THE SHELF
{{competitors|join}}
{{/if}}

{{#if localAngle}}
LOCAL / REGIONAL ANGLE
{{localAngle}}
{{/if}}

{{#if velocityData}}
PROOF
{{velocityData}}
{{/if}}
{{#unless velocityData}}
PROOF
None supplied — mark every data point as [INSERT] and tell me which proof {{retailer}} weighs most.
{{/unless}}

PROVIDE
1. {{retailer}} reality check — how this retailer's {{category|lower}} buyer is measured (sales per foot, margin, incrementality, local / emerging-brand programs, private-label mix), what their typical shopper looks like, and how that shopper overlaps with {{targetConsumer}}. Describe what is generally known about {{retailer}}; mark anything that may have changed with [VERIFY].
2. Private-label and incumbent reality — where {{retailer}}'s own brands or the incumbent national brands sit, and the specific gap {{brandName}} fills that they do not
3. The 3 arguments that matter most to {{retailer}}, ranked, each with the one sentence to say and the evidence to show
4. Local / regional angle — how to use it{{#if localAngle}}, built from the angle above{{/if}}{{#unless localAngle}} (suggest 3 ways to build one){{/unless}}; which {{retailer}} programs for local or emerging brands to ask about
5. Category review timing — how {{retailer}} typically handles resets and new-item windows for {{category|lower}}, and what to ask the buyer to confirm (mark [VERIFY])
6. The pitch — a 90-second verbal pitch in the founder's voice, then a 3-sentence version for a hallway or trade-show moment
7. Vendor requirements checklist — what {{retailer}} typically requires from new vendors (insurance, certifications, EDI, portal setup, distributor) with [VERIFY] on each
8. The ask — specific store count, set, placement and launch window appropriate for a brand at this stage with {{retailer}}
9. Red flags — 5 things that make {{retailer}} buyers say no to {{category|lower}} brands, and how {{brandName}} avoids each

RULES
Do not invent {{retailer}} statistics, margin targets, buyer names or program fees. Use typical ranges with [VERIFY] and tell me what to research.

{{#if avoid}}
AVOID
{{avoid|list}}
- "We'd love to partner" filler
- Generic pitches that could be sent to any retailer
{{/if}}
{{#unless avoid}}
AVOID
- "We'd love to partner" filler
- Generic pitches that could be sent to any retailer
- Consumer-ad tone
{{/unless}}`,
    tips: [
      "Run the Research a Retailer task in Perplexity first and paste its findings under PROOF for a sharper, current pitch.",
      "Follow up: “Now write the 3 slides that differ from my standard buyer deck for this retailer.”",
    ],
  },

  // ── DISTRIBUTOR PRESENTATION ───────────────────────────────────────────────
  {
    id: "retail-distributor-presentation",
    category: "retail",
    title: "Create Distributor Presentation",
    description: "Slide-by-slide distributor pitch (UNFI, KeHE, DSD or regional) that shows retail demand, program-cost awareness, margin structure and the marketing support that makes the line worth carrying.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gamma"],
    estimatedTime: "4 min to fill · 2 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine, 3 flavors", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "channels", label: "Current sales channels", type: "tags", placeholder: "DTC website, Amazon, 40 independent stores", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], default: "In local / regional retail", brandKey: "stage" },
      { id: "distributor", label: "Distributor type", type: "select", options: ["UNFI", "KeHE", "DSD / beverage distributor", "Regional specialty distributor", "Convenience / C-store distributor", "Foodservice distributor", "Other (describe in positioning)"], default: "KeHE", required: true },
      { id: "retailCommitments", label: "Retail demand you can show", type: "textarea", placeholder: "Sprouts SoCal approved 60 stores pending distribution; 40 independents buying direct; Whole Foods regional buyer interested", span: "full" },
      { id: "wholesaleCost", label: "Wholesale pricing", type: "text", placeholder: "$1.65 per unit to retail; distributor cost TBD" },
      { id: "caseConfig", label: "Case pack & logistics", type: "text", placeholder: "12 per case, 64 cases per pallet, 12-month shelf life, ships from Ohio" },
      { id: "marketingSupport", label: "Marketing & trade support", type: "textarea", placeholder: "Free fill for new doors, demo program, MCB / intro TPR budget, field marketing in 3 metros", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Consumer-ad language; pretending we have national scale", brandKey: "avoid" },
    ],
    template: `Act as a former category buyer and distributor account manager who has onboarded hundreds of emerging brands at {{distributor}}-type distributors.

Write the complete slide-by-slide content for a presentation from {{brandName}} ({{category|article}}) to {{distributor}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
MSRP: {{pricePoint}}
{{/if}}
{{#if wholesaleCost}}
Pricing: {{wholesaleCost}}
{{/if}}
{{#if caseConfig}}
Logistics: {{caseConfig}}
{{/if}}

POSITIONING
{{positioning}}

{{#if claims}}
KEY CLAIMS
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}
{{/if}}

BUSINESS CONTEXT
- Stage: {{stage}}
{{#if channels}}
- Current channels: {{channels|join}}
{{/if}}
{{#if retailCommitments}}
- Retail demand: {{retailCommitments}}
{{/if}}
{{#unless retailCommitments}}
- Retail demand: none supplied — a distributor will not carry a line without retail pull. Build the demand slide with [INSERT] slots and tell me what minimum pull {{distributor}} typically expects.
{{/unless}}
{{#if marketingSupport}}
- Marketing and trade support: {{marketingSupport}}
{{/if}}

DECK — WRITE EVERY SLIDE IN FULL
For each slide: title, one-sentence headline, 3 to 5 bullets or a table, speaker note, visual suggestion.
1. Title — brand, positioning line, date placeholder
2. Why this line — the category trend and the gap {{brandName}} fills vs. {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}the incumbents{{/unless}}
3. Product line — SKUs, sizes, UPCs, case pack, shelf life, certifications (table with [INSERT] for missing)
4. Retail demand — retailers committed, interested or already buying direct; why the distributor wins business by carrying us
5. Velocity and proof — existing sell-through, DTC signals, repeat rates ([INSERT] where missing)
6. Pricing structure — MSRP, suggested retailer cost, distributor cost and margin stack; show the math and mark distributor margin assumptions [CONFIRM]
7. Program-cost awareness — show we understand {{distributor}}'s typical new-vendor programs (set-up and slotting, free fill, MCB / off-invoice, promotional calendars, spoilage and chargebacks, marketing programs) and how we have budgeted for them; mark every cost figure [VERIFY — confirm current program terms]
8. Marketing and trade support — what we fund to pull product through (demos, TPR, digital, field team, retailer-specific programs)
9. Operations readiness — co-packer capacity, lead times, EDI / portal readiness, insurance, warehouse location, minimums
10. Launch plan — first 90 days with the distributor: onboarding, first retailers, promo windows, review checkpoint
11. The ask — specific: warehouse(s) / region, SKUs, launch month, retailers to target together
12. Appendix — spec sheet, contacts, certifications

THEN PROVIDE
13. A 45-second verbal open
14. The 5 questions a {{distributor}} category manager will ask that the inputs above cannot yet answer

RULES
Use only supplied numbers; write [INSERT] for anything missing. Never state {{distributor}}'s actual fees, margins or policies as fact — describe typical structures and mark [VERIFY]. Flag claims needing substantiation [VERIFY].

{{#if avoid}}
AVOID
{{avoid|list}}
- Consumer-ad language
- Overstating scale or readiness
{{/if}}
{{#unless avoid}}
AVOID
- Consumer-ad language
- Overstating scale or readiness
- More than 5 bullets per slide
{{/unless}}`,
    tips: [
      "Paste into Gamma for the deck. Then ask: “Write the one-page distributor line-card version of this.”",
      "Confirm current program fees with the distributor's new-vendor team before sending — terms change yearly.",
    ],
  },

  // ── COLD EMAIL TO BUYER ────────────────────────────────────────────────────
  {
    id: "retail-cold-email",
    category: "retail",
    title: "Write Cold Email to Buyer",
    description: "Three cold-email variants under 150 words with subject lines, plus a 4-step follow-up cadence, written the way buyers actually read email.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 30 sec in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "retailer", label: "Target retailer", type: "select", options: RETAILER_OPTIONS, default: "Sprouts Farmers Market", required: true },
      { id: "buyerName", label: "Buyer name (if known)", type: "text", placeholder: "Alex Rivera" },
      { id: "buyerRole", label: "Buyer role / category", type: "text", placeholder: "Regional grocery buyer, functional beverage" },
      { id: "proofPoint", label: "Your single strongest proof point", type: "textarea", placeholder: "4.2 units/store/week in 40 independents; or 2,000 DTC customers with 38% repeat", span: "full" },
      { id: "senderName", label: "Your name & title", type: "text", placeholder: "Jordan Lee, Co-founder" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "'I hope this finds you well'; attachments; long intros", brandKey: "avoid" },
    ],
    template: `Act as a former {{retailer}} category buyer who received 200 cold emails a week and now coaches emerging brands on the few that got a reply.

Write cold outreach from {{brandName}} ({{category|article}}) to {{#if buyerName}}{{buyerName}}, {{/if}}{{#if buyerRole}}{{buyerRole}} at {{/if}}{{retailer}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
MSRP: {{pricePoint}}
{{/if}}

POSITIONING
{{positioning}}

{{#if claims}}
KEY CLAIMS
{{claims|join}}
{{/if}}

{{#if proofPoint}}
STRONGEST PROOF (lead with this)
{{proofPoint}}
{{/if}}
{{#unless proofPoint}}
STRONGEST PROOF
None supplied — write the emails around the category gap and a low-risk ask, and mark where a proof point belongs with [INSERT PROOF].
{{/unless}}

{{#if brandVoice}}
VOICE
{{brandVoice}}
{{/if}}
{{#unless brandVoice}}
VOICE
Direct, specific, respectful of the buyer's time.
{{/unless}}

{{#if senderName}}
SIGN-OFF
{{senderName}}
{{/if}}

PROVIDE
1. Three email variants, each under 150 words, each with 3 subject-line options (under 50 characters):
   A. Proof-led — open with the strongest number or result
   B. Shopper-gap-led — open with what {{retailer}}'s {{category|lower}} shopper cannot find on shelf today
   C. Local / timing-led — open with a regional hook or a reset-window hook (mark the reset timing [VERIFY])
   Each email: one-line opener (no pleasantries), 2 to 3 sentences of why it fits {{retailer}}, one proof line, one low-friction ask (15-minute call or samples), signature.
2. A follow-up cadence — 4 touches after the first email (day 4, day 9, day 16, day 30): subject line, under 60 words each, one new piece of value per touch (sell sheet, sample offer, new data, a customer quote placeholder). Touch 4 is a polite close-the-loop.
3. A one-line "forwardable" summary the buyer can paste to a colleague
4. Subject-line do's and don'ts for {{retailer}} buyers (5 bullets)
5. Send-time and channel notes: when to email, when to use LinkedIn, when to go through a broker or the retailer's local-vendor portal (mark portal details [VERIFY])

RULES
No attachments mentioned in the first email — link a sell sheet instead. No invented numbers, retailer facts or buyer names. Do not make health claims beyond the key claims.

{{#if avoid}}
AVOID
{{avoid|list}}
- "I hope this email finds you well"
- Paragraph-long brand stories
{{/if}}
{{#unless avoid}}
AVOID
- "I hope this email finds you well"
- Paragraph-long brand stories
- Asking for a meeting before stating why it matters to the buyer
{{/unless}}`,
    tips: [
      "Send variant A if you have real velocity data; B if you are pre-retail. Track opens and switch subject lines after 20 sends.",
      "Follow up: “Rewrite variant B as a LinkedIn connection note under 300 characters.”",
    ],
  },

  // ── MEETING PREP ───────────────────────────────────────────────────────────
  {
    id: "retail-meeting-prep",
    category: "retail",
    title: "Prepare for Buyer Meeting",
    description: "A meeting-prep pack — agenda, 20 likely buyer questions with model answers, the numbers to know cold, what to bring and how to close.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "3 min to fill · 2 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], default: "In local / regional retail", brandKey: "stage" },
      { id: "retailer", label: "Retailer", type: "select", options: RETAILER_OPTIONS, default: "Whole Foods Market", required: true },
      { id: "meetingType", label: "Meeting type", type: "select", options: ["First intro meeting", "Line review / category review", "Follow-up after samples", "Virtual 30-minute call", "Trade show booth meeting", "Local / regional vendor day"], default: "First intro meeting" },
      { id: "velocityData", label: "Sales / velocity proof", type: "textarea", placeholder: "4.2 units/store/week across 40 stores; 38% DTC repeat rate", span: "full" },
      { id: "wholesaleCost", label: "Wholesale cost", type: "text", placeholder: "$1.65 per unit · $19.80 per case" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Scripted-sounding answers; over-promising", brandKey: "avoid" },
    ],
    template: `Act as a former {{retailer}} category buyer who now coaches emerging brands before their buyer meetings.

Prepare me for a {{meetingType|lower}} with the {{retailer}} {{category|lower}} buyer for {{brandName}}, {{category|article}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
MSRP: {{pricePoint}}
{{/if}}
{{#if wholesaleCost}}
Wholesale: {{wholesaleCost}}
{{/if}}

POSITIONING
{{positioning}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

{{#if claims}}
KEY CLAIMS
{{claims|join}}
{{/if}}

{{#if competitors}}
COMPETITORS ON THEIR SHELF
{{competitors|join}}
{{/if}}

BUSINESS CONTEXT
- Stage: {{stage}}
{{#if velocityData}}
- Proof: {{velocityData}}
{{/if}}
{{#unless velocityData}}
- Proof: none supplied — tell me what the buyer will accept instead and mark gaps [INSERT]
{{/unless}}

{{#if meetingType=First intro meeting}}
The goal of a first meeting is not a PO. It is to be remembered, get samples reviewed and secure a defined next step.
{{/if}}
{{#if meetingType=Line review / category review}}
This is a competitive review: the buyer is comparing {{brandName}} against incumbents and other new items for limited slots. Lead with incrementality and velocity.
{{/if}}
{{#if meetingType=Virtual 30-minute call}}
Plan for 20 minutes of content and 10 for questions; screen-share a short deck; send samples before the call.
{{/if}}
{{#if meetingType=Trade show booth meeting}}
Plan for 5 to 7 minutes; the buyer is standing; sampling and the one-page sell sheet do the work.
{{/if}}

PROVIDE
1. Meeting objective — the single outcome to leave with, and the fallback outcome
2. Agenda — timed, for the length typical of a {{meetingType|lower}}
3. 20 questions the {{retailer}} buyer is likely to ask, grouped (product, consumer, velocity and proof, pricing and margin, promo and marketing, logistics and compliance, company), each with a model answer in the founder's voice (2 to 4 sentences) using only the facts above. Where the answer needs a number not supplied, write the answer with [INSERT] and say what number to bring.
4. Numbers to know cold — a table: Metric | Your number (or [INSERT]) | Why the buyer cares | Category benchmark range at {{retailer}} (label as typical and [VERIFY])
5. What to bring — physical and digital checklist (samples, sell sheet, deck, spec sheet, pricing, insurance certificate, UPC list, promo calendar)
6. Opening 60 seconds — word for word
7. Closing — how to ask for the next step, and the exact follow-up date to propose
8. 5 things to never say in this meeting
9. Post-meeting — the 3 things to send within 24 hours

RULES
Use only supplied numbers. Do not invent {{retailer}} policies, margin targets or program fees — state typical ranges and mark [VERIFY].

{{#if avoid}}
AVOID
{{avoid|list}}
- Scripted, salesy phrasing
- Over-promising supply or marketing spend
{{/if}}
{{#unless avoid}}
AVOID
- Scripted, salesy phrasing
- Over-promising supply or marketing spend
- Criticizing competitors by name
{{/unless}}`,
    tips: [
      "Read the 20 answers out loud, then ask: “Quiz me — ask the 10 hardest ones one at a time and grade my answers.”",
      "Pair with Handle Buyer Objections for the pushback you will hear after your pitch.",
    ],
  },

  // ── OBJECTION HANDLING ─────────────────────────────────────────────────────
  {
    id: "retail-objections",
    category: "retail",
    title: "Handle Buyer Objections",
    description: "15 common buyer objections — slotting, velocity, price, shelf space, private label, \"come back with data\" — each with a tested response, proof to show and a fallback offer.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], default: "Launched — DTC only", brandKey: "stage" },
      { id: "retailer", label: "Retailer (or type)", type: "select", options: RETAILER_OPTIONS, default: "Independent / regional grocer", required: true },
      { id: "velocityData", label: "Sales / velocity proof", type: "textarea", placeholder: "4.2 units/store/week across 40 stores; 38% DTC repeat rate", span: "full" },
      { id: "promoSupport", label: "Support you can realistically offer", type: "textarea", placeholder: "Free fill on first order; 2 demos per store; intro TPR; guaranteed sale for 90 days", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Arguing with the buyer; promising what we cannot fund", brandKey: "avoid" },
    ],
    template: `Act as a former {{retailer}} category buyer who said no to hundreds of emerging {{category|lower}} brands and now coaches founders on how to turn those no's around.

Build an objection-handling guide for {{brandName}}, {{category|article}}, pitching {{retailer}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
MSRP: {{pricePoint}}
{{/if}}

POSITIONING
{{positioning}}

{{#if claims}}
KEY CLAIMS
{{claims|join}}
{{/if}}

{{#if competitors}}
INCUMBENTS ON SHELF
{{competitors|join}}
{{/if}}

WHAT WE CAN ACTUALLY OFFER
- Stage: {{stage}}
{{#if velocityData}}
- Proof: {{velocityData}}
{{/if}}
{{#unless velocityData}}
- Proof: none yet — responses must be honest about this and lean on low-risk structures
{{/unless}}
{{#if promoSupport}}
- Support: {{promoSupport}}
{{/if}}
{{#unless promoSupport}}
- Support: not specified — propose realistic options for a brand at this stage and mark costs [CONFIRM BUDGET]
{{/unless}}

PROVIDE
For each of the 15 objections below, give: (a) what the buyer really means, (b) a 2 to 4 sentence response in the founder's voice, (c) the proof or document to show, (d) a fallback offer if the response does not land, (e) a one-line note on when to accept the no and move on.
1. "We don't have shelf space."
2. "Your velocity isn't proven."
3. "Come back when you have data."
4. "The price is too high for our shopper."
5. "We already carry {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}the leading brands{{/unless}} — why do we need another?"
6. "Our private label covers this."
7. "What's your slotting / free-fill offer?"
8. "We only take new items at category review."
9. "You need to be with our distributor first."
10. "Your margin doesn't work for us."
11. "What marketing are you doing to drive traffic to our stores?"
12. "Can you supply all our stores / regions?"
13. "Your packaging won't stand out on our shelf."
14. "We tried a similar brand and it didn't work."
15. "Send me your sell sheet and I'll take a look." (the polite brush-off)

THEN PROVIDE
16. A table: Objection | Real risk to the buyer | Your strongest single counter
17. The 3 objections {{brandName}} is weakest on right now and what to fix before the next meeting
18. A 5-step framework for handling any objection not listed (acknowledge, clarify, reframe, prove, propose)

RULES
Use only supplied facts. Do not invent velocity numbers, margins, retailer policies or program fees — use [INSERT] and [VERIFY]. Fallback offers must be ones a brand at the "{{stage}}" stage can realistically fund; flag anything with cost implications [CONFIRM BUDGET].

{{#if avoid}}
AVOID
{{avoid|list}}
- Arguing with or correcting the buyer
- Promising spend you cannot fund
{{/if}}
{{#unless avoid}}
AVOID
- Arguing with or correcting the buyer
- Promising spend you cannot fund
- Disparaging competitors
{{/unless}}`,
    tips: [
      "Role-play it: “Be the buyer. Throw objections at me one at a time and critique my answers.”",
      "Paste #17 into your to-do list — those are the gaps to close before the next pitch.",
    ],
  },

  // ── FOLLOW-UP SEQUENCE ─────────────────────────────────────────────────────
  {
    id: "retail-follow-up",
    category: "retail",
    title: "Write Follow-Up Email Sequence",
    description: "A 4-touch post-meeting follow-up sequence that recaps, delivers what was promised, adds new value and closes the loop without nagging.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 30 sec in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "retailer", label: "Retailer", type: "select", options: RETAILER_OPTIONS, default: "Sprouts Farmers Market", required: true },
      { id: "buyerName", label: "Buyer name", type: "text", placeholder: "Alex Rivera" },
      { id: "meetingRecap", label: "What happened in the meeting", type: "textarea", placeholder: "Met at Expo West booth; buyer liked Citrus; asked for pricing and velocity from our 40 independents; mentioned spring reset", required: true, span: "full" },
      { id: "promisedItems", label: "What you promised to send", type: "text", placeholder: "Sell sheet, wholesale pricing, samples of all 3 flavors" },
      { id: "nextStep", label: "Next step you want", type: "select", options: ["Samples reviewed and feedback call", "Pricing approved and item set-up forms", "Test in a small store group", "Inclusion in the next category review", "Intro to the distributor contact", "Second meeting with the senior buyer"], default: "Test in a small store group" },
      { id: "senderName", label: "Your name & title", type: "text", placeholder: "Jordan Lee, Co-founder" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "'Just checking in'; guilt-tripping; long emails", brandKey: "avoid" },
    ],
    template: `Act as a former {{retailer}} category buyer who now coaches emerging brands on follow-up that gets a yes instead of silence.

Write a 4-touch follow-up email sequence from {{brandName}} ({{category|article}}) to {{#if buyerName}}{{buyerName}} at {{/if}}{{retailer}} after this meeting:

MEETING RECAP
{{meetingRecap}}
{{#if promisedItems}}
Promised to send: {{promisedItems}}
{{/if}}

DESIRED NEXT STEP
{{nextStep}}

{{#if productDescription}}
PRODUCT
{{productDescription}}
{{/if}}

POSITIONING
{{positioning}}

{{#if claims}}
KEY CLAIMS
{{claims|join}}
{{/if}}

{{#if brandVoice}}
VOICE
{{brandVoice}}
{{/if}}
{{#unless brandVoice}}
VOICE
Warm, direct, brief. Sounds like a person, not a sequence.
{{/unless}}

{{#if senderName}}
SIGN-OFF
{{senderName}}
{{/if}}

PROVIDE
Four emails, each with: send day, goal, 3 subject-line options (under 50 characters), body, one clear ask. Reference specifics from the meeting recap in every touch.
1. TOUCH 1 — same day, under 120 words: thank you, 2-line recap of what they said they liked, deliver{{#if promisedItems}} {{promisedItems}}{{/if}}{{#unless promisedItems}} the promised materials{{/unless}}, confirm the next step as "{{nextStep|lower}}" with a proposed date
2. TOUCH 2 — day 5, under 90 words: one new piece of value (a new data point placeholder, a customer quote placeholder, a relevant category observation) and a soft check on the next step
3. TOUCH 3 — day 12, under 80 words: make the next step easier — offer a smaller version of "{{nextStep|lower}}" and remove a reason to wait
4. TOUCH 4 — day 21, under 60 words: close the loop graciously, leave the door open, name the next natural moment (reset window, trade show, new data) — mark timing [VERIFY]

THEN PROVIDE
5. A table: Touch | Day | Subject | Ask | What to attach or link
6. Branch rules — what to send instead if the buyer replies with (a) "send samples", (b) "pricing is too high", (c) "not now, check back in Q3", (d) a referral to someone else
7. A 2-sentence internal note template to log the meeting and next action in a CRM or spreadsheet

RULES
No invented numbers, retailer dates or policies — use [INSERT] / [VERIFY]. Each email must stand alone if the earlier ones were ignored.

{{#if avoid}}
AVOID
{{avoid|list}}
- "Just checking in" or "bumping this to the top of your inbox"
- Guilt or pressure language
{{/if}}
{{#unless avoid}}
AVOID
- "Just checking in" or "bumping this to the top of your inbox"
- Guilt or pressure language
- Repeating the full pitch in every touch
{{/unless}}`,
    tips: [
      "Send touch 1 within 24 hours — buyers rank responsiveness as a proxy for how you will handle their orders.",
      "Follow up: “The buyer replied with [paste reply]. Draft my response.”",
    ],
  },

  // ── RETAILER RESEARCH ──────────────────────────────────────────────────────
  {
    id: "retail-retailer-research",
    category: "retail",
    title: "Research a Retailer",
    description: "A cited research brief on a retailer — buyer structure, category review windows, vendor requirements, recent news and emerging brands they have added — built for Perplexity or Gemini Deep Research.",
    outputType: "text",
    recommendedTools: ["perplexity-research", "gemini-research", "perplexity", "chatgpt"],
    estimatedTime: "1 min to fill · 3 min in Perplexity",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], default: "Launched — DTC only", brandKey: "stage" },
      { id: "retailer", label: "Retailer to research", type: "select", options: RETAILER_OPTIONS, default: "Whole Foods Market", required: true },
      { id: "region", label: "Region / division (optional)", type: "text", placeholder: "Rocky Mountain region; Texas; Northeast" },
      { id: "researchFocus", label: "Extra focus (optional)", type: "text", placeholder: "Local-supplier programs; how they handle functional beverages" },
      { id: "avoid", label: "Anything to exclude? (optional)", type: "textarea", placeholder: "Skip foodservice; ignore international stores; no pre-2023 news" },
    ],
    template: `Act as a senior CPG trade-research analyst and former category buyer. Research using current web sources and cite every factual claim with a link and date.

Research {{retailer}}{{#if region}} ({{region}}){{/if}} as a target account for {{brandName}}, {{category|article}}{{#if positioning}} positioned as: {{positioning}}{{/if}}.

{{#if productDescription}}
OUR PRODUCT
{{productDescription}}
{{/if}}
{{#if pricePoint}}
MSRP: {{pricePoint}}
{{/if}}
Stage: {{stage}}
{{#if competitors}}
Competitors we expect on their shelf: {{competitors|join}}
{{/if}}
{{#if researchFocus}}
Extra focus: {{researchFocus}}
{{/if}}

RESEARCH AND REPORT
1. Retailer snapshot — store count, formats, regions / divisions, ownership, recent financial or strategic direction, how {{category|lower}} is merchandised (aisle, cooler, checkout, end caps)
2. Buying structure — how {{retailer}} buys {{category|lower}}: national vs. regional / division buyers, category manager titles, whether local foragers or emerging-brand teams exist, and how new vendors typically get in (direct, broker, distributor, supplier portal). Name roles, not private individuals, unless publicly listed in a vendor-facing capacity.
3. Category review and reset windows — known or reported timing for {{category|lower}} category reviews, new-item submission windows and local / seasonal programs; state the source and confidence level
4. Vendor requirements — published supplier standards (ingredient and quality standards, certifications, insurance, EDI, packaging, sustainability policies, compliance portals), new-supplier programs or accelerators, typical fees or programs publicly discussed (slotting, free fill, promotional commitments) — cite or mark [UNCONFIRMED]
5. Recent news (last 12 months) — leadership changes, category strategy, private-label pushes, store openings or closures in{{#if region}} {{region}}{{/if}}{{#unless region}} key regions{{/unless}}, anything affecting {{category|lower}}
6. Emerging brands they have added — 8 to 12 emerging {{category|lower}} or adjacent brands {{retailer}} has added in the last 2 to 3 years, with what is known about how they got in (local program, distributor, accelerator) and price points
7. Private label — what {{retailer}}'s own brand offers in or near {{category|lower}}, and the gap left for {{brandName}}
8. Competitor presence — which of {{#if competitors}}{{competitors|join}}{{/if}}{{#unless competitors}}the leading {{category|lower}} brands{{/unless}} are carried, in what formats and at what shelf price where reported
9. Implications for {{brandName}} — 5 specific, evidence-backed recommendations for approach, timing, entry path and pricing relative to the set
10. Source list — every source with URL and date accessed; mark anything older than 18 months

FORMAT
Headed sections, tables where comparing items, inline citations after each claim. Separate confirmed facts from inference. If something cannot be found, say so explicitly rather than guessing.

{{#if avoid}}
AVOID
{{avoid|list}}
- Unsourced statistics or "it is widely known" claims
- Speculation about specific buyers' names or contact details
{{/if}}
{{#unless avoid}}
AVOID
- Unsourced statistics or "it is widely known" claims
- Speculation about specific buyers' names or contact details
- Advice that depends on data you could not verify
{{/unless}}`,
    tips: [
      "Run in Perplexity Deep Research or Gemini Deep Research so every fact comes with a link.",
      "Paste the findings into Create Retailer-Specific Pitch and Prepare for Buyer Meeting as context for sharper answers.",
    ],
  },

  // ── PROMOTIONAL PLAN ───────────────────────────────────────────────────────
  {
    id: "retail-promo-plan",
    category: "retail",
    title: "Build Promotional Plan",
    description: "A 12-month retail promotional plan — TPR / EDLC, demos, coupons and digital, with a trade-spend budget as a percent of revenue and a month-by-month calendar.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "google-sheets"],
    estimatedTime: "3 min to fill · 2 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "pricePoint", label: "MSRP", type: "text", placeholder: "$2.99 per can", required: true, brandKey: "pricePoint" },
      { id: "wholesaleCost", label: "Wholesale cost", type: "text", placeholder: "$1.65 per unit" },
      { id: "channels", label: "Channels in the plan", type: "tags", placeholder: "Natural grocery, Independents, Amazon", brandKey: "channels" },
      { id: "retailer", label: "Lead retailer for this plan", type: "select", options: RETAILER_OPTIONS, default: "Sprouts Farmers Market", required: true },
      { id: "storeCount", label: "Stores in the program", type: "text", placeholder: "60 stores" },
      { id: "launchMonth", label: "Launch / plan start month", type: "select", options: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"], default: "March" },
      { id: "tradeBudgetPct", label: "Trade spend budget (% of retail revenue)", type: "select", options: ["5%", "10%", "15%", "20%", "25%", "Not sure — recommend one"], default: "15%" },
      { id: "revenueTarget", label: "12-month retail revenue target (optional)", type: "text", placeholder: "$250,000 at retail" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Deep discounts that train shoppers to wait; promos we cannot fund", brandKey: "avoid" },
    ],
    template: `Act as a former {{retailer}} category buyer and trade-marketing lead who has built first-year promo plans for emerging {{category|lower}} brands.

Build a 12-month promotional plan for {{brandName}} ({{category|article}}) at {{retailer}}{{#if storeCount}}, {{storeCount}}{{/if}}, starting {{launchMonth}}.

PRODUCT AND PRICING
{{productDescription}}
- MSRP: {{pricePoint}}
{{#if wholesaleCost}}
- Wholesale: {{wholesaleCost}}
{{/if}}
{{#unless wholesaleCost}}
- Wholesale: not supplied — build the margin math with [INSERT WHOLESALE] and show the formula
{{/unless}}
{{#if channels}}
- Channels: {{channels|join}}
{{/if}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}
{{#if positioning}}
POSITIONING
{{positioning}}
{{/if}}

BUDGET
- Trade spend: {{tradeBudgetPct}} of retail revenue
{{#if tradeBudgetPct=Not sure — recommend one}}
- Recommend a percent with reasoning for a brand at this stage, and show the plan at that level
{{/if}}
{{#if revenueTarget}}
- 12-month retail revenue target: {{revenueTarget}} — size the budget from this
{{/if}}
{{#unless revenueTarget}}
- No revenue target supplied — express every cost as a % of revenue and per-store-per-month, and leave [INSERT] for dollar totals
{{/unless}}

PROVIDE
1. Promo strategy in 5 bullets — what the first year must prove to {{retailer}} (trial, repeat, velocity threshold) and how promotion supports each
2. Promo mechanics menu — a table: Tactic | How it works at {{retailer}} | Typical depth / cost structure (mark [VERIFY]) | Best for (trial vs. repeat) | Risk. Cover: intro TPR, EDLC / everyday price, demos and sampling, digital coupons and retailer app offers, paper / IRC coupons, end-cap or secondary placement, retailer ads and circulars, shopper-marketing and geo-targeted social, free fill / opening-order support
3. 12-month calendar starting {{launchMonth}} — a table: Month | Promo tactic(s) | Promo price (if any) | Depth % off MSRP | Support activity | Goal metric. Align to seasonality for {{category|lower}} and {{retailer}}'s typical promo cadence (mark cadence assumptions [VERIFY]).
4. Trade-spend budget — a table: Tactic | % of total trade budget | Cost as % of revenue | Per-store-per-month estimate. Total must equal {{tradeBudgetPct}}{{#if tradeBudgetPct=Not sure — recommend one}} (your recommended %){{/if}}. Show the formula, not just the result.
5. Margin check — unit economics on promo vs. everyday price: MSRP, promo price, wholesale, retailer margin %, your gross margin per unit after promo funding (formula shown; [INSERT] where numbers are missing)
6. Demo plan — number of demos, staffing approach, cost per demo assumption (mark [VERIFY]), target conversion and how to measure lift
7. Velocity targets — units per store per week needed to hold the shelf at each quarter, and which promo moves which number
8. Measurement — weekly / monthly scorecard fields, what to ask {{retailer}} or your distributor for, and the month-6 decision points
9. A paste-ready version of tables 3 and 4 formatted for Google Sheets

RULES
This is a planning model, not financial advice. Do not invent {{retailer}}'s program costs, demo fees or promo calendar — use typical ranges and mark [VERIFY]. Flag every assumption that should be confirmed with the founder's accountant or distributor before committing spend.

{{#if avoid}}
AVOID
{{avoid|list}}
- Promo depth that trains shoppers to only buy on deal
- Tactics a brand at this stage cannot fund
{{/if}}
{{#unless avoid}}
AVOID
- Promo depth that trains shoppers to only buy on deal
- Tactics a brand at this stage cannot fund
- More than 2 overlapping promos in one month
{{/unless}}`,
    tips: [
      "Paste table 4 into Google Sheets and link the percentages to your real revenue forecast so the budget updates automatically.",
      "Follow up: “Re-run the plan at 10% trade spend and show me what gets cut.”",
    ],
  },
];
